import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Check, Copy, ExternalLink } from 'lucide-react';

export interface SlackConnectResult {
  channel_name: string;
  invite_url?: string;
  already_existed: boolean;
  message: string;
}

interface SlackConnectResultModalProps {
  open: boolean;
  onClose: () => void;
  result: SlackConnectResult | null;
  /** The email the Slack Connect invite was sent to (the user's account email). */
  email?: string;
}

export function SlackConnectResultModal({ open, onClose, result, email }: SlackConnectResultModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result?.invite_url) return;
    try {
      await navigator.clipboard.writeText(result.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable; the user can still use the Open button.
    }
  };

  const title = result?.already_existed ? 'Slack Connect invite already sent' : 'Slack Connect invite sent';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="mb-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            We've sent a Slack Connect invite{email ? <> to <span className="font-medium text-foreground">{email}</span></> : ' to your account email'} to join{' '}
            <span className="font-medium text-foreground">#{result?.channel_name}</span>. Check your inbox (and spam) to accept it.
          </DialogDescription>
        </DialogHeader>

        {result?.invite_url && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Or join directly using this link:</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={result.invite_url}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                title={copied ? 'Copied' : 'Copy link'}
                className="flex-shrink-0"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {result?.invite_url && (
            <Button
              type="button"
              onClick={() => window.open(result.invite_url, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Join channel
            </Button>
          )}
          <Button type="button" variant={result?.invite_url ? 'outline' : 'default'} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
